/**
 * The two measurements behind the weak-connection message. Both hit static
 * files this frontend already serves from public/network-probe/ — never the
 * backend, and never anything the exercise uses — so they measure the path from
 * the patient's device to us, and the backend sees no traffic from them at all.
 *
 * Kept deliberately light, because they share the patient's connection with the
 * pose stream, the voice call and the reference video:
 *   - latency: a HEAD for a 3-byte file (a few hundred bytes of headers);
 *   - speed:   a 256 KB download, rarely (see use-network-quality for when).
 * Both are uncached and time out rather than hang.
 */

export const PING_URL = '/network-probe/ping.txt';
export const SAMPLE_URL = '/network-probe/sample.bin';
// public/network-probe/sample.bin. A response of any other size is not our
// file — see assertSampleResponse.
export const SAMPLE_BYTES = 262144;
const LATENCY_TIMEOUT_MS = 3000;
// Generous: the clock includes time spent queued behind the page's own
// requests, which says nothing about the connection. A sample that never got a
// byte in this long is reported as "no reading", not as 0 Mbps.
const DOWNLINK_TIMEOUT_MS = 20000;

export const bust = url => `${url}?t=${Date.now()}-${Math.random().toString(36).slice(2)}`;

/**
 * Request settings every probe shares.
 *
 * Same-origin credentials, not `omit`: a tunnel or proxy in front of the page
 * can gate requests on its own cookie and answer everything else with an HTML
 * interstitial — ngrok's free tier does exactly that, which is how a 10 Mbps
 * connection once read as ~0 (the "download" was a few KB of warning page).
 * The ngrok header skips that page; anywhere else it is an unknown header and
 * ignored. Same-origin, so it adds no CORS preflight.
 */
export const probeRequestInit = (signal, extra = {}) => ({
  cache: 'no-store',
  credentials: 'same-origin',
  headers: { 'ngrok-skip-browser-warning': '1' },
  signal,
  ...extra,
});

/**
 * Refuses a response that is not the probe file: an SPA fallback serving
 * index.html, a proxy's warning page, a deploy that predates the file. Measuring
 * those would report a confident, wrong number.
 */
export function assertSampleResponse(response) {
  if (!response.ok || !response.body) {
    throw new Error(`probe file answered ${response.status}`);
  }

  const type = response.headers.get('content-type') ?? '';
  const length = Number(response.headers.get('content-length'));

  if (type.includes('text/html') || (length && length !== SAMPLE_BYTES)) {
    throw new Error(
      `probe file not served as-is (${type || 'no type'}, ${length || '?'} bytes) — a proxy or SPA fallback is answering instead`
    );
  }
}

const withTimeout = async (timeoutMs, run) => {
  const controller = new AbortController();
  const timeoutId = window.setTimeout(() => controller.abort(), timeoutMs);

  try {
    return await run(controller.signal);
  } finally {
    window.clearTimeout(timeoutId);
  }
};

export const ping = signal =>
  fetch(bust(PING_URL), probeRequestInit(signal, { method: 'HEAD', priority: 'low' }));

/**
 * Round trip in ms, on a warm connection. Probes are 10s apart and static
 * servers close idle keep-alive sockets after ~5s, so a lone request would pay
 * a fresh TCP + TLS handshake each time — two to three extra round trips — and
 * read a healthy connection as slow. The first HEAD opens the connection; the
 * second, sent right behind it on the same socket, is the one timed.
 *
 * A probe that times out counts as the timeout itself — far over the
 * threshold, which is exactly what it was. A request that got no response at
 * all (no network, host unreachable) yields null, which the caller counts
 * towards "offline". An HTTP error still answered, so it still measures.
 */
export async function measureLatency() {
  try {
    return await withTimeout(LATENCY_TIMEOUT_MS * 2, async signal => {
      await ping(signal);
      const startedAt = performance.now();
      await ping(signal);

      return Math.round(performance.now() - startedAt);
    });
  } catch (error) {
    return error?.name === 'AbortError' ? LATENCY_TIMEOUT_MS : null;
  }
}

// Bytes let through before the clock starts: TCP slow start ramps the first
// few round trips up from ~14 KB, and timing those would under-read a fast but
// distant connection as slow.
const DOWNLINK_WARMUP_BYTES = 32 * 1024;

/**
 * Reads a probe body, counting bytes against time. The clock starts once
 * DOWNLINK_WARMUP_BYTES are in (past the slow-start ramp); if the body never
 * gets that far, it falls back to timing from the first chunk.
 */
export function createDownloadTimer() {
  let received = 0;
  let firstChunkAt = null;
  let firstChunkBytes = 0;
  let warmAt = null;
  let warmBytes = 0;
  let lastAt = null;

  return {
    add(byteLength) {
      const now = performance.now();

      if (firstChunkAt === null) {
        firstChunkAt = now;
        firstChunkBytes = byteLength;
      }

      received += byteLength;
      lastAt = now;

      if (warmAt === null && received >= DOWNLINK_WARMUP_BYTES) {
        warmAt = now;
        warmBytes = received;
      }
    },
    get received() {
      return received;
    },
    /** Mbps, Infinity when too fast to time, null when nothing arrived. */
    mbps() {
      if (!received) {
        return null;
      }

      const [from, fromBytes] =
        warmAt !== null && lastAt - warmAt > 0
          ? [warmAt, warmBytes]
          : [firstChunkAt, firstChunkBytes];
      const seconds = (lastAt - from) / 1000;

      // Everything in one go: too fast to time, so certainly not slow.
      if (seconds <= 0) {
        return Infinity;
      }

      return ((received - fromBytes) * 8) / seconds / 1e6;
    },
  };
}

/**
 * Download speed in Mbps for one 256 KB sample, or null when there is no
 * trustworthy reading (nothing arrived, or the response was not our file).
 */
export async function measureDownlink() {
  const timer = createDownloadTimer();

  try {
    await withTimeout(DOWNLINK_TIMEOUT_MS, async signal => {
      const response = await fetch(bust(SAMPLE_URL), probeRequestInit(signal, { priority: 'low' }));
      assertSampleResponse(response);

      const reader = response.body.getReader();

      for (;;) {
        const { done, value } = await reader.read();

        if (done) {
          return;
        }

        timer.add(value.byteLength);
      }
    });
  } catch (error) {
    // Timed out mid-download: what did arrive, over how long, is the answer.
    // Anything else — offline, not our file — is no reading at all.
    if (error?.name !== 'AbortError') {
      return null;
    }
  }

  return timer.mbps();
}

/** The browser's own speed estimate (Chromium / Android), or null elsewhere. */
export function readBrowserDownlink() {
  const downlink = navigator?.connection?.downlink;

  // 0 is reported by some browsers when they simply have no estimate yet.
  return typeof downlink === 'number' && downlink > 0 ? downlink : null;
}
